import { FormEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { DEVOPS_PLATFORMS, devOpsPlatformLabel } from '../../constants/DevOpsPlatform';
import {
  createProjectBacklogExternalLink,
  deleteProjectBacklogExternalLink,
  ProjectBacklogExternalLink,
  ProjectBacklogExternalLinkUpsertPayload,
  updateProjectBacklogExternalLink,
} from '../../services/commercialProjectsApi';
import { parseDevOpsUrl } from '../../utils/parseDevOpsUrl';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { DeleteIconButton } from '../ui/DeleteIconButton';
import { EditIconButton } from '../ui/EditIconButton';
import { StatusBadge } from '../ui/StatusBadge';

const EXTERNAL_TYPE_OPTIONS = [
  { value: '', label: 'Não informado' },
  { value: 'User Story', label: 'User Story' },
  { value: 'Task', label: 'Task' },
  { value: 'Bug', label: 'Bug' },
  { value: 'Epic', label: 'Epic' },
  { value: 'Issue', label: 'Issue' },
];

type ProjectBacklogExternalLinksPanelProps = {
  projectId: string;
  backlogItemId: string;
  backlogTitle: string;
  links: ProjectBacklogExternalLink[];
  canWrite: boolean;
  onChanged: () => void;
};

const emptyForm = (): ProjectBacklogExternalLinkUpsertPayload => ({
  platform: 'AzureDevOps',
  externalTitle: '',
  externalUrl: '',
  externalId: '',
  externalType: '',
  notes: '',
});

export function ProjectBacklogExternalLinksPanel({
  projectId,
  backlogItemId,
  backlogTitle,
  links,
  canWrite,
  onChanged,
}: ProjectBacklogExternalLinksPanelProps) {
  const [formOpen, setFormOpen] = useState(canWrite);
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  function openCreateForm(): void {
    setEditingLinkId(null);
    setForm(emptyForm());
    setFormOpen(true);
  }

  function openEditForm(link: ProjectBacklogExternalLink): void {
    setEditingLinkId(link.id);
    setForm({
      platform: link.platform,
      externalTitle: link.externalTitle ?? '',
      externalUrl: link.externalUrl ?? '',
      externalId: link.externalId ?? '',
      externalType: link.externalType ?? '',
      notes: link.notes ?? '',
    });
    setFormOpen(true);
  }

  function closeForm(): void {
    setFormOpen(false);
    setEditingLinkId(null);
    setForm(emptyForm());
  }

  function handleUrlChange(url: string): void {
    setForm((current) => {
      const next = { ...current, externalUrl: url };
      const parsed = parseDevOpsUrl(url);
      if (!parsed) return next;
      return {
        ...next,
        platform: parsed.platform,
        externalId: parsed.externalId ?? next.externalId,
        externalType: parsed.externalType ?? next.externalType,
      };
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    try {
      const payload: ProjectBacklogExternalLinkUpsertPayload = {
        platform: form.platform,
        externalTitle: form.externalTitle?.trim() || undefined,
        externalUrl: form.externalUrl?.trim() || undefined,
        externalId: form.externalId?.trim() || undefined,
        externalType: form.externalType?.trim() || undefined,
        notes: form.notes?.trim() || undefined,
      };

      if (editingLinkId) {
        await updateProjectBacklogExternalLink(projectId, backlogItemId, editingLinkId, payload);
        toast.success('Vínculo externo atualizado.');
      } else {
        await createProjectBacklogExternalLink(projectId, backlogItemId, payload);
        toast.success('Vínculo externo planejado.');
      }

      closeForm();
      onChanged();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar vínculo externo.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(link: ProjectBacklogExternalLink): Promise<void> {
    try {
      await deleteProjectBacklogExternalLink(projectId, backlogItemId, link.id);
      toast.success('Vínculo externo removido.');
      onChanged();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao remover vínculo externo.'));
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-medium text-secondary-600">Rastreabilidade externa</p>
        {canWrite && !formOpen ? (
          <button type="button" className="erp-btn-secondary text-xs" onClick={openCreateForm}>
            Planejar vínculo
          </button>
        ) : null}
      </div>

      {links.length === 0 ? (
        <p className="text-xs text-secondary-500">Nenhum vínculo com plataforma externa.</p>
      ) : (
        <ul className="space-y-1">
          {links.map((link) => (
            <li
              key={link.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-secondary-50 px-2 py-1.5"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone={link.status === 'Linked' ? 'success' : 'caution'}>
                    {link.status === 'Linked' ? 'Vinculado' : 'Pendente'}
                  </StatusBadge>
                  <span className="text-xs text-secondary-500">{devOpsPlatformLabel(link.platform)}</span>
                  {link.externalType ? (
                    <span className="text-xs text-secondary-500">{link.externalType}</span>
                  ) : null}
                </div>
                <p className="truncate text-sm text-secondary-900">{link.displayTitle || backlogTitle}</p>
                {link.externalId ? (
                  <p className="text-xs text-secondary-500">ID: {link.externalId}</p>
                ) : null}
                {link.externalUrl ? (
                  <a
                    href={link.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary-600 hover:underline"
                  >
                    Abrir na plataforma
                  </a>
                ) : null}
              </div>
              {canWrite ? (
                <div className="flex shrink-0 gap-1">
                  <EditIconButton
                    label={link.status === 'Linked' ? 'Editar vínculo' : 'Completar vínculo'}
                    onClick={() => openEditForm(link)}
                  />
                  <DeleteIconButton
                    label="Remover vínculo"
                    onClick={() => void handleDelete(link)}
                  />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {formOpen && canWrite ? (
        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-2 rounded-md border border-secondary-200 p-3">
          <p className="text-sm font-medium text-secondary-900">
            {editingLinkId ? 'Completar vínculo externo' : 'Planejar vínculo externo'}
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="erp-label">
              Plataforma
              <select
                className="erp-input mt-1"
                value={form.platform}
                onChange={(e) => setForm((c) => ({ ...c, platform: e.target.value }))}
                required
              >
                {DEVOPS_PLATFORMS.map((platform) => (
                  <option key={platform.value} value={platform.value}>
                    {platform.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label">
              Tipo externo
              <select
                className="erp-input mt-1"
                value={form.externalType ?? ''}
                onChange={(e) => setForm((c) => ({ ...c, externalType: e.target.value }))}
              >
                {EXTERNAL_TYPE_OPTIONS.map((option) => (
                  <option key={option.value || 'none'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label sm:col-span-2">
              Título do vínculo (opcional)
              <input
                className="erp-input mt-1"
                placeholder={backlogTitle}
                value={form.externalTitle ?? ''}
                onChange={(e) => setForm((c) => ({ ...c, externalTitle: e.target.value }))}
              />
            </label>
            <label className="erp-label sm:col-span-2">
              URL externa (opcional)
              <input
                className="erp-input mt-1"
                type="url"
                placeholder="Cole o link do Azure DevOps, Jira…"
                value={form.externalUrl ?? ''}
                onChange={(e) => handleUrlChange(e.target.value)}
              />
            </label>
            <label className="erp-label">
              ID externo (opcional)
              <input
                className="erp-input mt-1"
                value={form.externalId ?? ''}
                onChange={(e) => setForm((c) => ({ ...c, externalId: e.target.value }))}
              />
            </label>
            <label className="erp-label sm:col-span-2">
              Notas (opcional)
              <input
                className="erp-input mt-1"
                value={form.notes ?? ''}
                onChange={(e) => setForm((c) => ({ ...c, notes: e.target.value }))}
              />
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="erp-btn-primary" disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
            <button type="button" className="erp-btn-secondary" onClick={closeForm} disabled={saving}>
              Cancelar
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
